export type PlanFeature = string | [string, string]

export interface PlanCardProps {
  name: string
  tagline: string
  price: string
  period: string
  subPrice?: string
  ctaText: string
  ctaVariant: 'primary' | 'secondary'
  ctaDisabled: boolean
  onCta: () => void
  featuresHeading: string
  features: PlanFeature[]
  featured?: boolean
  /** Optional content rendered between the price/sub-price block and the CTA button (e.g. seat stepper). */
  extraSlot?: React.ReactNode
  /** CSS order class for responsive reordering (mobile: Pro first; desktop: Free | Pro | Team). */
  orderClass?: string
}

export function PlanCard({
  name,
  tagline,
  price,
  period,
  subPrice,
  ctaText,
  ctaVariant,
  ctaDisabled,
  onCta,
  featuresHeading,
  features,
  featured,
  extraSlot,
  orderClass,
}: PlanCardProps) {
  return (
    <div
      className={[
        'relative flex flex-col rounded-2xl bg-white p-5 md:p-7',
        featured ? 'border-2 border-sky-500' : 'border border-gray-200',
        orderClass ?? '',
      ].join(' ')}
    >
      <div
        className={[
          'mb-1 inline-flex h-7 items-center self-start text-xs font-medium uppercase tracking-wider',
          featured
            ? 'rounded-md bg-sky-500 px-2.5 text-white tracking-[0.12em]'
            : 'text-gray-900',
        ].join(' ')}
      >
        {name}
      </div>
      <p className="mb-5 text-sm text-gray-600 md:mb-6 md:min-h-[40px]">{tagline}</p>

      <div className="mb-1 flex items-baseline gap-1.5">
        <span className="text-4xl font-medium tracking-tight text-gray-900 md:text-5xl">{price}</span>
        <span className="text-sm text-gray-600">{period}</span>
      </div>
      <p className="mb-5 text-xs text-gray-500 md:mb-6 md:min-h-[20px]">{subPrice ?? ''}</p>

      {extraSlot}

      <button
        type="button"
        onClick={onCta}
        disabled={ctaDisabled}
        className={[
          'mb-2 block w-full rounded-lg px-4 py-3 text-center text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
          ctaVariant === 'primary'
            ? 'bg-sky-500 text-white hover:bg-sky-600'
            : 'border border-gray-300 text-gray-900 hover:border-gray-900',
        ].join(' ')}
      >
        {ctaText}
      </button>
      <p className="mb-6 text-center text-[11px] leading-snug text-gray-500">
        By continuing, you agree to our{' '}
        <a
          href="https://xavior.ai/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-600 hover:underline"
        >
          Terms
        </a>{' '}
        and{' '}
        <a
          href="https://xavior.ai/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-600 hover:underline"
        >
          Privacy Policy
        </a>
        .
      </p>

      <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
        {featuresHeading}
      </p>
      <ul className="flex flex-col gap-3">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm leading-snug text-gray-900">
            <svg
              className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-sky-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>
              {Array.isArray(f) ? (
                <>
                  <strong className="font-semibold">{f[0]}</strong> {f[1]}
                </>
              ) : (
                f
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
